import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, File, Image as ImageIcon, Table, FileText, Search, Trash2, CheckSquare, Square, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { confirmDialog } from "@/lib/dialogs";

const ICONS = {
  pdf: FileText,
  docx: FileText,
  xlsx: Table,
  image: ImageIcon,
  text: File,
  other: File,
};

export default function DocumentsPanel({ project, selected, onSelectedChange, onMutated }) {
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listDocs(project.id, q ? { q } : {});
      setDocs(list);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [project.id, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      try {
        await api.uploadDoc(project.id, f);
        ok++;
      } catch {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    await load();
    onMutated?.();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropRef.current?.classList.remove("bg-[#0D1F12]");
    upload(e.dataTransfer.files);
  };

  const toggle = (id) => {
    if (selected.includes(id)) onSelectedChange(selected.filter((x) => x !== id));
    else onSelectedChange([...selected, id]);
  };

  const removeDoc = async (e, d) => {
    e.stopPropagation();
    const ok = await confirmDialog({
      title: `Delete document?`,
      message: d.filename,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteDoc(d.id);
      onSelectedChange(selected.filter((x) => x !== d.id));
      toast.success("Document deleted");
      await load();
      onMutated?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openPreview = async (e, d) => {
    e.stopPropagation();
    try {
      const full = await api.getDoc(d.id);
      setPreview(full);
    } catch {
      toast.error("Preview failed");
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="documents-panel">
      {/* Toolbar */}
      <div className="border-b border-[#2D7544] px-6 py-3 flex items-center gap-3">
        <div className="flex items-center gap-2 flex-1 max-w-md border-b border-[#2D7544] px-2">
          <Search size={14} className="text-[#66B07A]" />
          <input
            data-testid="doc-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full py-2 text-sm bg-transparent focus:outline-none placeholder:text-[#3D8657]"
            placeholder="Search filenames, content, tags…"
          />
        </div>
        <button
          data-testid="select-all-btn"
          onClick={() => onSelectedChange(docs.map((d) => d.id))}
          className="dv-btn-ghost"
        >
          Select all
        </button>
        <button data-testid="clear-selection-btn" onClick={() => onSelectedChange([])} className="dv-btn-ghost">
          Clear
        </button>
        <button data-testid="upload-trigger-btn" onClick={() => inputRef.current?.click()} className="dv-btn-primary flex items-center gap-2">
          <Upload size={14} /> Upload
        </button>
        <input
          ref={inputRef}
          data-testid="file-input"
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Drop zone */}
        <div
          ref={dropRef}
          data-testid="drop-zone"
          onDragOver={(e) => {
            e.preventDefault();
            dropRef.current?.classList.add("bg-[#0D1F12]");
          }}
          onDragLeave={() => dropRef.current?.classList.remove("bg-[#0D1F12]")}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-[#2D7544] bg-[#000000] p-8 flex flex-col items-center justify-center cursor-pointer transition-colors group"
        >
          <Upload size={28} className="text-[#00FF41] mb-3" />
          <div className="font-heading font-extrabold text-xl tracking-tighter">
            {uploading ? "UPLOADING…" : "Drag & drop or click to upload"}
          </div>
          <div className="text-xs text-[#66B07A] mt-1">
            PDF · DOCX · XLSX · CSV · TXT · PNG/JPG (OCR)
          </div>
        </div>

        {/* Grid */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="dv-overline">All Documents · {docs.length}</div>
            {loading && <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A]">Loading…</div>}
          </div>

          {docs.length === 0 && !loading ? (
            <div className="border border-dashed border-[#2D7544] p-10 text-center text-sm text-[#66B07A]" data-testid="no-docs">
              No documents yet. Upload some to start reviewing with AI.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="doc-grid">
              {docs.map((d) => {
                const Icon = ICONS[d.kind] || File;
                const isSel = selected.includes(d.id);
                return (
                  <div
                    key={d.id}
                    data-testid={`doc-card-${d.id}`}
                    onClick={() => toggle(d.id)}
                    className={`border border-[#2D7544] p-4 cursor-pointer transition-all bg-[#050805] flex flex-col gap-2 ${
                      isSel ? "shadow-[4px_4px_0px_#00FFC9]" : "hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#00FF41]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="border border-[#2D7544] p-1.5"><Icon size={14} /></div>
                        <div className="min-w-0">
                          <div className="font-bold text-sm truncate" title={d.filename}>{d.filename}</div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-[#66B07A]">
                            {d.kind} · {formatSize(d.size)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          data-testid={`preview-doc-${d.id}`}
                          onClick={(e) => openPreview(e, d)}
                          className="p-1.5 border border-transparent hover:border-[#2D7544] hover:bg-[#0D1F12]"
                          title="Preview"
                        >
                          <Eye size={13} />
                        </button>
                        <button
                          data-testid={`delete-doc-${d.id}`}
                          onClick={(e) => removeDoc(e, d)}
                          className="p-1.5 border border-transparent hover:border-[#FF3344] hover:text-[#FF3344]"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>

                    {d.extracted_preview ? (
                      <div className="text-xs text-[#66B07A] leading-relaxed line-clamp-3 font-mono">
                        {d.extracted_preview.slice(0, 200) || "No extracted text."}
                      </div>
                    ) : (
                      <div className="text-xs text-[#3D8657] italic">No extracted text.</div>
                    )}

                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex flex-wrap gap-1">
                        {(d.tags || []).map((t) => (
                          <span key={t} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-[#2D7544]">
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${isSel ? "text-[#00FFC9]" : "text-[#66B07A]"}`}>
                        {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                        {isSel ? "Selected" : "Select"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function DocPreview({ doc, onClose }) {
  return (
    <div className="fixed inset-0 z-40 bg-[#000000]/80 flex items-center justify-center p-4" onClick={onClose} data-testid="preview-overlay">
      <div className="bg-[#050805] border border-[#2D7544] w-full max-w-3xl max-h-[85vh] flex flex-col shadow-[6px_6px_0px_#00FF41]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2D7544] px-5 py-3">
          <div className="min-w-0">
            <div className="dv-overline">Document Preview</div>
            <div className="font-heading text-lg font-extrabold tracking-tighter truncate">{doc.filename}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#0D1F12]" data-testid="close-preview">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 font-mono text-xs whitespace-pre-wrap leading-relaxed text-[#00FF41]">
          {doc.extracted_text || "[No extracted text available]"}
        </div>
        <div className="border-t border-[#2D7544] px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-[#66B07A]">
          {doc.kind} · {formatSize(doc.size)} · ID {doc.id.slice(0, 8)}
        </div>
      </div>
    </div>
  );
}

function formatSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
